// src/components/Chat/ChatPanel.js - With Resize + Fullscreen
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, History, RefreshCw, Paperclip, Maximize2, Minimize2 } from 'lucide-react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { useChat } from '../../hooks/useChat';
import { CHAT_CONFIG } from '../../utils/constants';
import { useFileUpload } from '../../hooks/useFileUpload';

const btnStyle = (rgb, disabled = false) => ({
  background: `rgba(${rgb}, 0.2)`,
  border: `1px solid rgba(${rgb}, 0.3)`,
  borderRadius: '6px',
  color: `rgb(${rgb})`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  padding: '6px',
  display: 'flex', alignItems: 'center', gap: '4px',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease'
});

const ChatPanel = ({
  isOpen, onClose, userName, companyName,
  fileAnalysisResult, onDataChanged, onFileAnalyzed
}) => {
  const { messages, isLoading, showHistory, sendMessage, loadChatHistory, startFreshChat, setMessages } = useChat(onDataChanged);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [size, setSize] = useState({ width: CHAT_CONFIG.DEFAULT_WIDTH, height: CHAT_CONFIG.DEFAULT_HEIGHT });
  const isResizing = useRef(false);
  const resizeStart = useRef({});
  const panelRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastFileAnalysisRef = useRef(null);
  const [dragCounter, setDragCounter] = useState(0);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.width, h: size.height };
  }, [size]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const dx = resizeStart.current.x - e.clientX;
      const dy = resizeStart.current.y - e.clientY;
      setSize({
        width: Math.max(CHAT_CONFIG.MIN_WIDTH, Math.min(900, resizeStart.current.w + dx)),
        height: Math.max(CHAT_CONFIG.MIN_HEIGHT, Math.min(window.innerHeight * 0.9, resizeStart.current.h + dy))
      });
    };
    const onMouseUp = () => { isResizing.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const storedAnalysis = localStorage.getItem('aiAssistantResponse');
      if (storedAnalysis) {
        try {
          const data = JSON.parse(storedAnalysis);
          setTimeout(() => {
            const msg = { role: 'assistant', content: data.response, timestamp: data.timestamp || new Date().toISOString(), sender: 'Chairman', coresUsed: data.coresUsed, feature: data.feature };
            setMessages(prev => prev.some(m => m.content === msg.content && m.timestamp === msg.timestamp) ? prev : [...prev, msg]);
            localStorage.removeItem('aiAssistantResponse');
          }, 100);
        } catch (e) { console.error('Error loading stored analysis:', e); }
      }
    }
  }, [isOpen, setMessages]);

  const handleFileAnalyzed = (result) => { if (onFileAnalyzed) onFileAnalyzed(result); };
  const { isDragging, isProcessing, handleDragOver, handleDragLeave, handleDrop } = useFileUpload(handleFileAnalyzed);

  const addImageMessage = (file) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setMessages(prev => [...prev, { role: 'user', content: 'Analyzing image...', image: e.target.result, timestamp: new Date().toISOString(), id: Date.now() + Math.random(), isProcessing: true, sender: 'user' }]);
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    addImageMessage(file);
    await handleDrop({ preventDefault: () => {}, dataTransfer: { files: [file] } });
    e.target.value = '';
  };

  useEffect(() => {
    if (fileAnalysisResult && isOpen) {
      const analysisKey = `${fileAnalysisResult.metadata?.fileName || 'file'}-${fileAnalysisResult.metadata?.timestamp || Date.now()}`;
      if (lastFileAnalysisRef.current !== analysisKey) {
        lastFileAnalysisRef.current = analysisKey;
        if (fileAnalysisResult.success && fileAnalysisResult.analysis?.rounds) {
          const rounds = fileAnalysisResult.analysis.rounds;
          setMessages(prev => prev.map(msg => msg.isProcessing ? { ...msg, isProcessing: false, content: '' } : msg));
          const analysisMessages = [];
          rounds.forEach((round, index) => {
            if (round.speaker === 'ryder' && round.type === 'initial_analysis') {
              analysisMessages.push({ role: 'assistant', content: `👁️ **Ryder Vision Analysis**\n\nI've analyzed your image "${fileAnalysisResult.analysis.fileName}":\n\n${round.content}`, timestamp: round.timestamp || new Date().toISOString(), id: Date.now() + index, sender: 'ryder' });
            } else if (round.speaker === 'aetheris' && round.type === 'synthesis') {
              analysisMessages.push({ role: 'assistant', content: `🧠 **Aetheris Synthesis**\n\n${round.content}`, timestamp: round.timestamp || new Date().toISOString(), id: Date.now() + index + 100, sender: 'aetheris' });
            }
          });
          if (analysisMessages.length > 0) {
            setMessages(prev => [...prev, ...analysisMessages.filter(msg => !prev.some(e => e.content === msg.content && e.sender === msg.sender))]);
          }
        } else {
          const analysisContent = fileAnalysisResult.analysis?.analysis?.rounds?.[0]?.content || fileAnalysisResult.analysis?.ryderAnalysis || (typeof fileAnalysisResult.analysis === 'string' ? fileAnalysisResult.analysis : 'Analysis completed');
          setMessages(prev => [...prev, { role: 'assistant', content: `🔍 **Analysis Complete**\n\n${analysisContent}\n\nHow would you like me to help with this analysis?`, timestamp: new Date().toISOString(), id: Date.now(), sender: 'assistant' }]);
        }
      }
    }
  }, [fileAnalysisResult, isOpen, setMessages]);

  if (!isOpen) return null;

  const panelStyle = isFullscreen
    ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', borderRadius: 0, zIndex: 2000 }
    : { position: 'fixed', bottom: '100px', right: '24px', width: `${size.width}px`, height: `${size.height}px`, minWidth: `${CHAT_CONFIG.MIN_WIDTH}px`, maxWidth: '900px', minHeight: `${CHAT_CONFIG.MIN_HEIGHT}px`, maxHeight: '90vh', borderRadius: '12px', zIndex: 1000 };

  return (
    <div
      ref={panelRef}
      style={{
        ...panelStyle,
        background: isDragging ? 'rgba(59, 130, 246, 0.4)' : 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px)',
        border: isDragging ? '2px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.3)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}
      onDragEnter={(e) => { e.preventDefault(); setDragCounter(p => p + 1); if (dragCounter === 0) handleDragOver(e); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragLeave={(e) => { e.preventDefault(); setDragCounter(p => p - 1); if (dragCounter === 1) handleDragLeave(e); }}
      onDrop={async (e) => { e.preventDefault(); setDragCounter(0); handleDragLeave(e); if (e.dataTransfer.files.length > 0) addImageMessage(e.dataTransfer.files[0]); await handleDrop(e); }}
    >
      {/* Resize handle - top left corner */}
      {!isFullscreen && (
        <div onMouseDown={onResizeMouseDown} style={{ position: 'absolute', top: 0, left: 0, width: '20px', height: '20px', cursor: 'nw-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Drag to resize">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1 9L9 1M1 5L5 1M5 9L9 5" stroke="rgba(148,163,184,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,.csv,.xlsx,.xls,.json,.txt,.pdf" onChange={handleFileSelect} style={{ display: 'none' }} />

      {isDragging && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(59, 130, 246, 0.95)', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📎</div>
          <h3 style={{ color: '#ffffff', margin: 0, fontSize: '20px' }}>Drop your file here</h3>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginTop: '8px', fontSize: '14px' }}>Images, documents, CSV, Excel, JSON, and more</p>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(59, 130, 246, 0.3)', flexShrink: 0 }}>
        <div>
          <h3 style={{ color: '#3b82f6', fontSize: '18px', fontWeight: '600', margin: 0 }}>Chat with Chairman AI</h3>
          {userName && <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>Building for {userName} • {companyName || 'Your Company'}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => fileInputRef.current?.click()} disabled={isProcessing} style={btnStyle('168, 85, 247', isProcessing)} title="Upload file"><Paperclip size={14} /></button>
          {!showHistory ? (
            <button onClick={() => loadChatHistory(10)} style={btnStyle('34, 197, 94')} title="Load history"><History size={14} /></button>
          ) : (
            <>
              <button onClick={() => loadChatHistory(10, true)} style={btnStyle('59, 130, 246')} title="Load older"><History size={14} /><span style={{ fontSize: '10px' }}>More</span></button>
              <button onClick={startFreshChat} style={btnStyle('249, 115, 22')} title="Fresh chat"><RefreshCw size={14} /></button>
            </>
          )}
          <button onClick={() => setIsFullscreen(f => !f)} style={btnStyle('245, 158, 11')} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={onClose} style={btnStyle('239, 68, 68')} title="Close"><X size={16} /></button>
        </div>
      </div>

      {showHistory && (
        <div style={{ padding: '8px 20px', background: 'rgba(34, 197, 94, 0.1)', borderBottom: '1px solid rgba(34, 197, 94, 0.2)', color: '#22c55e', fontSize: '12px', textAlign: 'center', flexShrink: 0 }}>
          📜 Showing recent chat history
        </div>
      )}

      <MessageList messages={messages} isLoading={isLoading} style={{ flex: 1 }} />
      <MessageInput onSendMessage={sendMessage} disabled={isLoading} userName={userName} companyName={companyName} />
    </div>
  );
};

export default ChatPanel;
